/* ═══════════════════════════════════════════════════════════════
   The Designer — the document tools of the row-2 toolbar.

   Everything here is mouse-first and visible (DESIGN §1): find &
   replace has a button, not just Ctrl+F; images and tables insert
   from plain buttons.

   R5: each control is its OWN toolbar item so the user can pin and
   unpin them individually (Tools ▸ Customise toolbar…). Each one
   subscribes to the editor itself rather than making the whole
   Designer re-render on every keystroke.

   Smart blocks are CUT (MASTERS_LADDER_PLAN §0, R5) with ONE exception
   (Josh 2026-10-02): Related sections, which inserts from the button
   below. No other smart kind has an insert affordance anywhere; legacy
   blocks already in a document still render read-only and still resolve
   at export.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import { Image as ImageIcon, ListChecks, Search, Table } from 'lucide-react';
import { ToolbarButton } from '../editor-chrome/Toolbar';
import { insertImageBlock, insertTableBlock, pickImageFile } from './commands';
import { closeFind, getFindState, openFind } from './search';
import { FindReplace } from './FindReplace';
import './tools.css';

/** Re-render this control (and only this control) on every editor
 *  transaction — plugin state such as "is find open" speaks through them. */
function useEditorTick(editor: Editor | null) {
  const [, force] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => {
    if (!editor) return;
    editor.on('transaction', force);
    return () => {
      editor.off('transaction', force);
    };
  }, [editor]);
}

/* ─── Find ─── */

export function FindButton({ editor }: { editor: Editor | null }) {
  // The button's pressed state tracks the plugin, whichever of Ctrl+F / the
  // button / Esc changed it.
  useEditorTick(editor);
  const findOpen = editor ? !!getFindState(editor.state)?.open : false;
  const toggleFind = () => {
    if (!editor) return;
    const dispatch = editor.view.dispatch.bind(editor.view);
    if (findOpen) {
      closeFind(editor.state, dispatch);
      editor.commands.focus();
    } else {
      openFind(editor.state, dispatch);
    }
  };
  return (
    <ToolbarButton
      icon={Search}
      label="Find & replace"
      tooltip="Ctrl+F · Ctrl+H"
      aria-label="Find and replace"
      active={findOpen}
      onClick={toggleFind}
      disabled={!editor}
    />
  );
}

/* ─── Insert image / table ─── */

export function InsertImageButton({
  editor,
  canEdit,
}: {
  editor: Editor | null;
  canEdit: boolean;
}) {
  const onInsertImage = async () => {
    if (!editor) return;
    const img = await pickImageFile();
    if (!img) return;
    insertImageBlock(editor.state, editor.view.dispatch.bind(editor.view), img.dataUrl, img.name);
    editor.commands.focus();
  };
  return (
    <ToolbarButton
      icon={ImageIcon}
      label="Image"
      aria-label="Insert image"
      tooltip="Insert image after the current paragraph"
      onClick={() => void onInsertImage()}
      disabled={!editor || !canEdit}
    />
  );
}

export function InsertTableButton({
  editor,
  canEdit,
}: {
  editor: Editor | null;
  canEdit: boolean;
}) {
  return (
    <ToolbarButton
      icon={Table}
      label="Table"
      aria-label="Insert table"
      tooltip="Insert a 3×3 table after the current paragraph"
      onClick={() => {
        if (!editor) return;
        insertTableBlock(editor.state, editor.view.dispatch.bind(editor.view));
        editor.commands.focus();
      }}
      disabled={!editor || !canEdit}
    />
  );
}

/** Related sections — the host decides what the new block is seeded with
 *  (a project spec: the project's sections; a master: nothing yet). */
export function InsertRelatedSectionsButton({
  editor,
  canEdit,
  onInsert,
}: {
  editor: Editor | null;
  canEdit: boolean;
  onInsert: () => void;
}) {
  return (
    <ToolbarButton
      icon={ListChecks}
      label="Related sections"
      aria-label="Insert related sections"
      tooltip="Insert a list of this project's other sections after the current paragraph"
      onClick={onInsert}
      disabled={!editor || !canEdit}
    />
  );
}

/* ─── The find & replace bar ───
   Hosted once, always — Ctrl+F must work whether or not the Find button is
   pinned to the toolbar. It hangs under whatever element it is anchored to. */

export function FindReplaceHost({
  editor,
  canEdit,
  anchorRef,
}: {
  editor: Editor | null;
  canEdit: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
}) {
  if (!editor) return null;
  return <FindReplace editor={editor} canEdit={canEdit} anchorRef={anchorRef} />;
}
